import React, { useCallback, useEffect, useRef, useState } from 'react';
import Reactions from '../../components/Reactions';
import { apiFetch, getToken, API_BASE } from '../../services/apiClient';
import AttachButton from '../../components/AttachButton';
import { FieldwireTasks } from './FieldwirePanel';
import JobNumberLink from '../../components/JobNumberLink';

// ─────────────────────────────────────────────────────────────────────────────
// The Miscellaneous and Custom card: a title, updates, photos and documents.
//
// It exists for the thing that happened on a job and has to be written down where
// it can be found again — the week an unmarked utility got hit, say — without it
// becoming a change order or a charge against the job. Miscellaneous is the fixed
// name; Custom takes whatever you call it, for the job-specific thing nobody knew
// about when the job started.
//
// Deliberately plain. It stores against the same tables every other card uses —
// workflow photos, sub-job files, the job's daily updates — so anything filed here
// shows up in the job's own photo and update feeds rather than a private corner.
// ─────────────────────────────────────────────────────────────────────────────

const lbl = { fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 };
const box = { border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 11px', fontSize: 14, width: '100%', boxSizing: 'border-box', background: '#fff' };
const btn = { padding: '9px 14px', borderRadius: 8, border: 'none', background: '#e87722', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' };
const ghost = { padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const when = s => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';

export default function NoteCardModal({ card, pmName, onClose, onChanged, inline = false }) {
  const sjId = card.sub_job_id || card.id;
  const projectId = card.project_id;

  const [title, setTitle] = useState(card.name || '');
  const [notes, setNotes] = useState(card.notes || '');
  const [savedAt, setSavedAt] = useState('');
  const [photos, setPhotos] = useState([]);
  const [files, setFiles] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const photoRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    apiFetch(`/sub-jobs/${sjId}/photos`).then(r => setPhotos(Array.isArray(r) ? r : [])).catch(() => {});
    apiFetch(`/sub-jobs/${sjId}/files`).then(r => setFiles(Array.isArray(r) ? r : [])).catch(() => {});
    if (projectId) apiFetch(`/projects/${projectId}/daily-updates`).then(r => setUpdates(Array.isArray(r) ? r : [])).catch(() => {});
  }, [sjId, projectId]);
  useEffect(() => { load(); }, [load]);

  // Title and notes save on blur rather than on every keystroke — this card is
  // written into once and read many times, so a save per character is noise.
  async function saveCard(fields) {
    try {
      await apiFetch(`/projects/${projectId}/sub-jobs/${sjId}`, { method: 'PATCH', body: JSON.stringify(fields) });
      setSavedAt('Saved');
      setTimeout(() => setSavedAt(''), 1800);
      if (onChanged) onChanged();
    } catch { setSavedAt('Could not save'); }
  }

  async function post() {
    if (!text.trim() || !projectId) return;
    setBusy('update');
    try {
      // sub_job_id routes it to this card's activity as well as the job's updates,
      // so an entry made here is findable from the card it belongs to.
      await apiFetch(`/projects/${projectId}/daily-updates`, {
        method: 'POST',
        body: JSON.stringify({ notes: text.trim(), sub_job_id: sjId }),
      });
      setText('');
      load();
    } catch { /* the text stays in the box */ }
    setBusy('');
  }

  async function upload(kind, list) {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    setBusy(kind);
    try {
      if (kind === 'photo') {
        const fd = new FormData();
        picked.forEach(f => fd.append('photos', f));
        await fetch(`${API_BASE}/sub-jobs/${sjId}/photos`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
      } else {
        // The files route takes one at a time, so send them in sequence.
        for (const f of picked) {
          const fd = new FormData();
          fd.append('file', f);
          await fetch(`${API_BASE}/sub-jobs/${sjId}/files`, { method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd });
        }
      }
      load();
    } catch { /* nothing added */ }
    setBusy('');
  }

  async function removePhoto(id) {
    if (!window.confirm('Remove this photo?')) return;
    try { await apiFetch(`/sub-jobs/${sjId}/photos/${id}`, { method: 'DELETE' }); load(); } catch {}
  }
  async function removeFile(id) {
    if (!window.confirm('Remove this document?')) return;
    try { await apiFetch(`/sub-jobs/${sjId}/files/${id}`, { method: 'DELETE' }); load(); } catch {}
  }

  const body = (
    <div style={{ padding: inline ? 0 : 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ ...lbl, marginBottom: 4 }}>Title</div>
        <input style={box} value={title} onChange={e => setTitle(e.target.value)}
          onBlur={() => title.trim() && title !== card.name && saveCard({ name: title.trim() })}
          placeholder="What is this about?" />
      </div>

      <div>
        <div style={{ ...lbl, marginBottom: 4 }}>What happened</div>
        <textarea style={{ ...box, minHeight: 90, resize: 'vertical' }} value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={() => notes !== (card.notes || '') && saveCard({ notes })}
          placeholder="Describe it — dates, who was there, what was found." />
        {savedAt && <div style={{ fontSize: 11.5, color: savedAt === 'Saved' ? '#15803d' : '#b91c1c', marginTop: 4 }}>{savedAt}</div>}
      </div>

      {/* Fieldwire pins whose category is mapped to this card (quiet when there are none). */}
      <FieldwireTasks projectId={projectId} subJobId={sjId} canEdit />

      {/* Photos */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={lbl}>Photos{photos.length ? ` (${photos.length})` : ''}</span>
          <span style={{ flex: 1 }} />
          <AttachButton multiple accept="image" variant="plain" icon="📷"
            label={busy === 'photo' ? 'Adding…' : 'Add photos'} disabled={busy === 'photo'}
            style={{ padding: '6px 12px', fontSize: 13 }} onFiles={fs => upload('photo', fs)} />
        </div>
        {photos.length === 0 ? (
          <div style={{ fontSize: 12.5, color: '#9ca3af' }}>No photos yet.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
            {photos.map(p => (
              <div key={p.id} style={{ position: 'relative' }}>
                <a href={`${API_BASE.replace('/api', '')}${p.url}`} target="_blank" rel="noreferrer">
                  <img src={`${API_BASE.replace('/api', '')}${p.url}`} alt={p.original_name || 'photo'}
                    style={{ width: '100%', height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb' }} />
                </a>
                <button onClick={() => removePhoto(p.id)} title="Remove"
                  style={{ position: 'absolute', top: 3, right: 3, width: 20, height: 20, borderRadius: 10, border: 'none',
                    background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 13, lineHeight: 1, cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Documents */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <span style={lbl}>Documents{files.length ? ` (${files.length})` : ''}</span>
          <span style={{ flex: 1 }} />
          <AttachButton multiple accept="pdf" variant="plain" icon="📄"
            label={busy === 'file' ? 'Adding…' : 'Add PDFs'} disabled={busy === 'file'}
            style={{ padding: '6px 12px', fontSize: 13 }} onFiles={fs => upload('file', fs)} />
        </div>
        {files.length === 0 ? (
          <div style={{ fontSize: 12.5, color: '#9ca3af' }}>No documents yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: '#f3f4f6', border: '1px solid #f3f4f6', borderRadius: 8, overflow: 'hidden' }}>
            {files.map(f => (
              <div key={f.id} style={{ background: '#fff', padding: '9px 12px', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {f.original_name || f.filename}
                </span>
                <span style={{ fontSize: 11.5, color: '#9ca3af', whiteSpace: 'nowrap' }}>{when(f.created_at)}</span>
                <a href={`${API_BASE.replace('/api', '')}/uploads/${f.filename}`} target="_blank" rel="noreferrer"
                  style={{ fontSize: 12, color: '#c2410c', fontWeight: 700, textDecoration: 'none' }}>Open</a>
                <button onClick={() => removeFile(f.id)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 15, cursor: 'pointer', padding: 0 }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Updates */}
      <div>
        <div style={{ ...lbl, marginBottom: 6 }}>Updates{updates.length ? ` (${updates.length})` : ''}</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <input style={box} value={text} onChange={e => setText(e.target.value)}
            placeholder="Add an update…" onKeyDown={e => { if (e.key === 'Enter') post(); }} />
          <button style={btn} onClick={post} disabled={busy === 'update' || !text.trim()}>Post</button>
        </div>
        {updates.length === 0 ? (
          <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Nothing posted on this job yet.</div>
        ) : updates.slice(0, 12).map(u => (
          <div key={u.id} style={{ borderLeft: '3px solid #e5e7eb', paddingLeft: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 700 }}>
              {u.author_name || 'Someone'}
              <span style={{ fontWeight: 500 }}> · {when(u.created_at)}</span>
            </div>
            <div style={{ fontSize: 13, color: '#111', marginTop: 2, whiteSpace: 'pre-wrap' }}>{u.notes}</div>
            <Reactions type="update" id={u.id} reactions={u.reactions} />
          </div>
        ))}
      </div>
    </div>
  );

  if (inline) return <div style={{ display: 'block' }}>{body}</div>;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 'max(env(safe-area-inset-bottom), 12px)',
        paddingLeft: 12, paddingRight: 12 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#f9fafb', borderRadius: 14, width: 560, maxWidth: '100%',
          maxHeight: 'min(92vh, calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px))',
          display: 'flex', flexDirection: 'column', boxShadow: '0 16px 60px rgba(0,0,0,0.28)' }}>
        <div style={{ background: '#4a5568', borderRadius: '14px 14px 0 0', padding: '14px 18px', color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 800 }}><JobNumberLink projectId={projectId}>{card.project_number || 'Job'}</JobNumberLink></div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 2 }}>{title || card.name}</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 3 }}>
              {card.client_name ? `🏢 ${card.client_name} · ` : ''}👤 {pmName ? pmName(card.pm_id) : ''}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff',
              fontSize: 20, cursor: 'pointer', lineHeight: 1, width: 40, height: 40, borderRadius: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
        </div>
        <div style={{ overflowY: 'auto' }}>{body}</div>
        <div style={{ borderTop: '1px solid #e5e7eb', background: '#fff', padding: '10px 14px',
          paddingBottom: 'max(env(safe-area-inset-bottom), 10px)', borderRadius: '0 0 14px 14px' }}>
          <button onClick={onClose}
            style={{ width: '100%', minHeight: 48, borderRadius: 10, border: '1.5px solid #d1d5db', background: '#fff',
              color: '#374151', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
}
